import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import api from '../services/api';
import Turnstile from '../components/Turnstile';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Cloudflare Turnstile (only when VITE_TURNSTILE_SITE_KEY is set); single use.
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [setupAvailable, setSetupAvailable] = useState(false);
  // Authenticator (TOTP) step: shown once the password is accepted and MFA is enabled.
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaMethod, setMfaMethod] = useState('totp');
  const [mfaHint, setMfaHint] = useState('');
  const [resending, setResending] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const expired = searchParams.get('expired');

  // Check if setup is available (no admin users exist)
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await api.get('/admin/auth/setup-status');
        setSetupAvailable(response.data.setupAvailable === true);
      } catch (err) {
        // If check fails, hide setup link for security
        setSetupAvailable(false);
      }
    };
    checkSetupStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, turnstileToken, totpCode || null);
      if (result.success) {
        navigate('/');
      } else if (result.mfaRequired) {
        setMfaRequired(true);
        setMfaMethod(result.method || 'totp');
        setMfaHint(result.hint || '');
        setTotpCode('');
        if (result.error) setError(result.error);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    }
  };

  // SMS method: signing in again without a code texts a fresh one (the API
  // re-sends at most every 30 seconds).
  const resendCode = async () => {
    setResending(true);
    setError('');
    try {
      const result = await login(email, password, null, null);
      if (result.mfaRequired) {
        setMfaHint(result.hint || mfaHint);
        if (result.error) setError(result.error);
      } else if (result.error) {
        setError(result.error);
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <img src="/mark.svg" alt="Practice Manager Pro" className="w-16 h-16" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-navy font-display">
            Admin Portal
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Practice Manager Pro Administration
          </p>
        </div>

        {expired && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <p className="text-sm text-yellow-700">
              Your session has expired. Please log in again.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="label">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="label">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-500 font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {mfaRequired && (
              <div>
                <label htmlFor="totpCode" className="label">
                  {mfaMethod === 'sms' ? 'Code from the text message' : 'Authenticator code'}
                </label>
                <input
                  id="totpCode"
                  name="totpCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  className="input tracking-widest"
                  placeholder="123456"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {mfaMethod === 'sms'
                    ? `We texted a 6-digit code to ${mfaHint || 'your mobile'}. It expires in 10 minutes.`
                    : 'Enter the 6-digit code from your authenticator app.'}
                </p>
                {mfaMethod === 'sms' && (
                  <button
                    type="button"
                    onClick={resendCode}
                    disabled={resending || loading}
                    className="mt-2 text-xs text-primary-600 hover:underline disabled:opacity-50"
                  >
                    {resending ? 'Sending…' : "Didn't get it? Send a new code"}
                  </button>
                )}
              </div>
            )}
          </div>

          <Turnstile key={turnstileKey} onToken={setTurnstileToken} />

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                mfaRequired ? 'Verify and sign in' : 'Sign in'
              )}
            </button>
          </div>
        </form>

        {setupAvailable && (
          <p className="text-center text-sm text-gray-500">
            First time?{' '}
            <Link to="/setup" className="text-primary-600 hover:text-primary-500 font-medium">
              Set up admin account
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
